import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { User, KeyRound, Loader2, Save, Eye, EyeOff, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyProfilePage() {
  const { user, token, refreshUser } = useAuth();

  // ---------- Name update ----------
  const [name, setName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);

  const handleSaveName = async (e) => {
    e.preventDefault();
    const cleaned = name.trim();
    if (!cleaned || cleaned.length < 2) {
      toast.error('الاسم يجب أن يكون حرفين على الأقل');
      return;
    }
    if (cleaned === user?.name) {
      toast.info('لم يتم تغيير الاسم');
      return;
    }
    setSavingName(true);
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: cleaned }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'فشل في تحديث الاسم');
      toast.success('تم تحديث الاسم بنجاح');
      refreshUser?.();
    } catch (err) {
      toast.error(err.message || 'فشل في تحديث الاسم');
    } finally {
      setSavingName(false);
    }
  };

  // ---------- Password change ----------
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      toast.error('يرجى ملء جميع الحقول');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('كلمتا المرور غير متطابقتين');
      return;
    }
    setSavingPwd(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'فشل في تغيير كلمة المرور');
      toast.success('تم تغيير كلمة المرور بنجاح');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.message || 'فشل في تغيير كلمة المرور');
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6" dir="rtl">
      <div className="max-w-2xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">حسابي</h1>
            <p className="text-gray-500 text-sm">إدارة بيانات حسابك الشخصية</p>
          </div>
        </div>

        {/* Read-only info */}
        <div className="bg-white rounded-2xl border shadow-sm p-4 sm:p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-gray-400" />
            المعلومات الأساسية
          </h2>
          <div className="space-y-3">
            <InfoRow label="البريد الإلكتروني" value={user?.email} />
            <InfoRow label="الدور" value={roleLabel(user?.role)} />
            {user?.company_name && <InfoRow label="الشركة" value={user.company_name} />}
          </div>
          <p className="mt-4 text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
            هذه المعلومات لا يمكن تعديلها. للتغيير، تواصل مع مدير النظام.
          </p>
        </div>

        {/* Update name */}
        <form onSubmit={handleSaveName} className="bg-white rounded-2xl border shadow-sm p-4 sm:p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-4 h-4 text-blue-600" />
            تعديل الاسم
          </h2>
          <label className="block text-sm font-medium text-gray-700 mb-2">الاسم</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            data-testid="profile-name-input"
            className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-base focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
            placeholder="اسمك"
          />
          <button
            type="submit"
            disabled={savingName}
            data-testid="profile-save-name-btn"
            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 h-12 rounded-xl font-semibold disabled:opacity-50 transition"
          >
            {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            حفظ الاسم
          </button>
        </form>

        {/* Change password */}
        <form onSubmit={handleChangePassword} className="bg-white rounded-2xl border shadow-sm p-4 sm:p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-amber-600" />
            تغيير كلمة المرور
          </h2>

          <PasswordField
            label="كلمة المرور الحالية"
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showCurrent}
            toggleShow={() => setShowCurrent(s => !s)}
            testId="current-password-input"
          />

          <PasswordField
            label="كلمة المرور الجديدة"
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            toggleShow={() => setShowNew(s => !s)}
            hint="6 أحرف على الأقل"
            testId="new-password-input"
          />

          <PasswordField
            label="تأكيد كلمة المرور الجديدة"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showNew}
            toggleShow={() => setShowNew(s => !s)}
            testId="confirm-password-input"
          />

          <button
            type="submit"
            disabled={savingPwd}
            data-testid="profile-change-password-btn"
            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 h-12 rounded-xl font-semibold disabled:opacity-50 transition"
          >
            {savingPwd ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            تغيير كلمة المرور
          </button>
        </form>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, toggleShow, hint, testId }) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          data-testid={testId}
          className="w-full h-12 px-4 pl-11 border-2 border-gray-200 rounded-xl text-base focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none"
          placeholder="••••••••"
        />
        <button
          type="button"
          onClick={toggleShow}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}

function roleLabel(role) {
  const map = {
    super_admin: 'مدير النظام الرئيسي',
    company_admin: 'مدير الشركة',
    employee: 'موظف',
  };
  return map[role] || role;
}
